/**
 * What the connector can do to the Customer.io destination, and what each
 * outcome is called.
 *
 * Kept apart from `mutations.ts` so a module that only needs the taxonomy — the
 * setup-guide side effect keys its release on `Delete` — does not pull in the OAuth
 * popup and the API client with it.
 */

/** What to do with the destination. `Default` is the empty string the API treats as create. */
export const SegmentAuthAction = {
  Default: '',
  Create: 'create',
  Enable: 'enable',
  Check: 'check',
  Delete: 'delete',
  Disable: 'disable',
} as const;

export type SegmentAuthAction = (typeof SegmentAuthAction)[keyof typeof SegmentAuthAction];

/** What the attempt did, as the screen reports it. */
export const SegmentAuthResult = {
  Created: 'created',
  Enabled: 'enabled',
  Deleted: 'deleted',
  Disabled: 'disabled',
  Errored: 'errored',
} as const;

export type SegmentAuthResult = (typeof SegmentAuthResult)[keyof typeof SegmentAuthResult];

/**
 * Which result a successful action produces. `Default` and `Create` both create,
 * and `Check` reports on a destination that exists, so all three read as created.
 */
export function mapActionToResult(action: SegmentAuthAction): SegmentAuthResult {
  switch (action) {
    case SegmentAuthAction.Delete:
      return SegmentAuthResult.Deleted;
    case SegmentAuthAction.Disable:
      return SegmentAuthResult.Disabled;
    case SegmentAuthAction.Enable:
      return SegmentAuthResult.Enabled;
    case SegmentAuthAction.Default:
    case SegmentAuthAction.Create:
    case SegmentAuthAction.Check:
      return SegmentAuthResult.Created;
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}
